import { Modal, Text, Group, Button, Stack } from '@mantine/core';
import { IconTrash, IconAlertTriangle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { DeleteConfirmationDialogProps } from './DeleteConfirmationDialog.types';
import { FormFooter } from '@/components/common/form';
import { FONT_WEIGHT_SEMIBOLD } from '@/constants/ui';
import {
  Z_INDEX_MODAL_CRITICAL,
  Z_INDEX_MODAL_CRITICAL_BACKDROP,
} from '@/constants/zIndex';

const MESSAGE_KEYS: Record<DeleteConfirmationDialogProps['itemType'], string> =
  {
    task: 'ui.deleteConfirmation.messageTask',
    geometry: 'ui.deleteConfirmation.messageGeometry',
    geometries: 'ui.deleteConfirmation.messageGeometries',
    note: 'ui.deleteConfirmation.messageNote',
    obstruction: 'ui.deleteConfirmation.messageObstruction',
    shareLink: 'ui.deleteConfirmation.messageShareLink',
  };

export const DeleteConfirmationDialog = ({
  isOpen,
  itemName,
  itemType,
  onCancel,
  onConfirm,
}: DeleteConfirmationDialogProps) => {
  const { t } = useTranslation();

  const message = t(MESSAGE_KEYS[itemType], { itemName });

  return (
    <Modal
      opened={isOpen}
      onClose={onCancel}
      title={
        <Group gap="xs">
          <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
          <Text fw={FONT_WEIGHT_SEMIBOLD}>
            {t('ui.deleteConfirmation.title')}
          </Text>
        </Group>
      }
      centered
      size="sm"
      closeOnClickOutside={false}
      closeOnEscape={true}
      trapFocus={true}
      withCloseButton={true}
      closeButtonProps={{
        'aria-label': t('ui.deleteDialog.closeDeleteDialog'),
      }}
      zIndex={Z_INDEX_MODAL_CRITICAL}
      styles={{
        overlay: {
          zIndex: Z_INDEX_MODAL_CRITICAL_BACKDROP,
        },
      }}
    >
      <Stack gap="md">
        <Text size="sm">{message}</Text>
        <Text size="xs" c="dimmed">
          {t('ui.deleteConfirmation.warning')}
        </Text>

        <FormFooter
          leftActions={
            <Button variant="default" onClick={onCancel}>
              {t('ui.deleteConfirmation.cancel')}
            </Button>
          }
          rightActions={
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={onConfirm}
            >
              {t('ui.deleteConfirmation.delete')}
            </Button>
          }
        />
      </Stack>
    </Modal>
  );
};
